import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, withPrefixWildcard } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import {
  ensureSkuIndex,
  findExactCodeProductIds,
  findExactCodeProductIdsFromCards,
  findExactCard,
  findProductIdsForSkuPrefix,
  isShortModelCode,
  pinExactCards,
  setSkuSupplementPending,
  shouldApplyDelayedExactPin,
  shouldAwaitExactCodeIndex,
  skuSearchIdsQuery,
} from '@theme/fixatech-sku-index';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** Guards SKU pending class so an aborted fetch cannot clear a newer one. */
  #skuPendingEpoch = 0;

  /** Bumped at the start of each `#getSearchResults` so a newer search cancels a late pin. */
  #searchToken = 0;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      // Inline mode: show dropdown on click, hide on click-outside
      this.refs.searchInput.addEventListener('click', this.#showDropdown, { signal });
      document.addEventListener('click', this.#handleClickOutside, { signal });
    }

    this.refs.form?.addEventListener('submit', this.#handleFormSubmit, { signal });
    this.refs.viewAllButton?.addEventListener('click', this.#handleViewAll, { signal });

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  get #contentWrapper() {
    return this.refs.predictiveSearchResults.closest('.predictive-search-form__content-wrapper');
  }

  #showDropdown = () => {
    this.#contentWrapper?.classList.add('is-open');
    if (!this.#emptyStateLoaded) {
      this.#loadEmptyState();
    }
  };

  #hideDropdown = () => {
    this.#contentWrapper?.classList.remove('is-open');
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClickOutside = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) {
      this.#hideDropdown();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.#resetSearch();
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    if (index >= 0) {
      activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      this.refs.searchInput.focus();
    }
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (this.dialog) {
        this.#resetSearch();
      } else {
        this.#hideDropdown();
        this.refs.searchInput.blur();
      }
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          event.preventDefault();
          window.location.href = this.#buildResultsUrl(this.refs.searchInput.value);
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showDropdown();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  #buildResultsUrl(term) {
    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set('q', withPrefixWildcard(term));
    searchUrl.searchParams.set('type', 'product');
    return searchUrl.toString();
  }

  #handleViewAll = (event) => {
    event.preventDefault();
    const term = this.refs.searchInput.value.trim();
    if (!term) return;
    window.location.href = this.#buildResultsUrl(term);
  };

  #handleFormSubmit = (event) => {
    event.preventDefault();
    const term = this.refs.searchInput.value.trim();
    if (!term) return;
    window.location.href = this.#buildResultsUrl(term);
  };

  /**
   * Fetch search results using the section renderer and update the results container.
   * Short model codes skip suggest.json (it never mid-matches spaced SKUs) and
   * go straight to the products.json SKU/barcode index → id: morph.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const searchToken = ++this.#searchToken;
    const abortController = this.#createAbortController();

    try {
      if (isShortModelCode(searchTerm)) {
        await this.#getSkuIndexResults(searchTerm, abortController);
        return;
      }

      if (shouldAwaitExactCodeIndex(searchTerm)) ensureSkuIndex();

      this.#skuPendingEpoch += 1;
      setSkuSupplementPending(false);

      const query = withPrefixWildcard(searchTerm);
      const url = new URL(Theme.routes.predictive_search_url, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[limit_scope]', 'each');
      // Predictive search defaults to searching only title/product_type/variants.title/vendor,
      // while the full /search results page also searches SKU, barcode, tags and description.
      // That mismatch made the dropdown show "no results" for queries (e.g. part numbers / SKUs)
      // that the results page still matched. Expand predictive search to the same field set so
      // both stay in parity. Note: metafields (e.g. custom.manufacturer_part_number) are NOT
      // indexable here — mirror those into tags to make them searchable.
      // Drop `body` if typeahead relevance gets too noisy.
      url.searchParams.set(
        'resources[options][fields]',
        'title,product_type,variants.title,vendor,variants.sku,variants.barcode,tag,body'
      );

      const { predictiveSearchResults } = this.refs;
      let resultsMarkup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      if (abortController.signal.aborted) return;

      if (!this.#markupHasProductResults(resultsMarkup)) {
        const searchUrl = new URL(Theme.routes.search_url, location.origin);
        searchUrl.searchParams.set('q', query);
        searchUrl.searchParams.set('type', 'product');
        const fallbackMarkup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, searchUrl);
        if (abortController.signal.aborted) return;
        if (fallbackMarkup) resultsMarkup = fallbackMarkup;
      }

      if (!resultsMarkup || abortController.signal.aborted) return;

      morph(predictiveSearchResults, resultsMarkup);
      this.#resetScrollPositions();
      const list = predictiveSearchResults.querySelector('.predictive-search-results__wrapper-products');
      if (list) {
        const renderedExactIds = findExactCodeProductIdsFromCards(list, searchTerm);
        if (renderedExactIds.length) pinExactCards(list, renderedExactIds);
      }
      await this.#pinExactProductCards(findExactCodeProductIds(searchTerm), abortController);
      this.#pinWhenIndexReady(searchTerm, searchToken, abortController);
    } catch (error) {
      if (abortController.signal.aborted) return;
      throw error;
    }
  }

  /**
   * Resolve DCN-class queries from the SKU/barcode index. Empty copy stays
   * hidden until this settles; results lists are never display:none'd.
   * @param {string} searchTerm
   * @param {AbortController} abortController
   */
  async #getSkuIndexResults(searchTerm, abortController) {
    const epoch = ++this.#skuPendingEpoch;
    setSkuSupplementPending(true);
    try {
      let paintedPartial = false;
      /** @type {Promise<void>} */
      let partialMorph = Promise.resolve();

      const ids = await findProductIdsForSkuPrefix(searchTerm, {
        onPartial: (partialIds) => {
          if (paintedPartial || !partialIds.length) return;
          paintedPartial = true;
          partialMorph = this.#renderSkuSupplement(partialIds, abortController);
        },
      });

      await partialMorph.catch(() => {});
      if (abortController.signal.aborted) return;

      if (ids.length) {
        await this.#renderSkuSupplement(ids, abortController);
        return;
      }

      const searchUrl = new URL(Theme.routes.search_url, location.origin);
      searchUrl.searchParams.set('q', searchTerm);
      searchUrl.searchParams.set('type', 'product');
      const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, searchUrl);
      if (!markup || abortController.signal.aborted) return;

      morph(this.refs.predictiveSearchResults, markup);
      this.#resetScrollPositions();
    } finally {
      if (this.#skuPendingEpoch === epoch) {
        setSkuSupplementPending(false);
      }
    }
  }

  /**
   * After morph, if the exact map is still building, pin when it lands — no extra wait
   * before today's results. Compare the captured term + search token, not the input
   * value (`#resetSearch` on a cold 390 modal empties `#cmdk-input`).
   * @param {string} searchTerm
   * @param {number} searchToken
   * @param {AbortController} abortController
   */
  #pinWhenIndexReady(searchTerm, searchToken, abortController) {
    if (!shouldAwaitExactCodeIndex(searchTerm)) return;
    if (findExactCodeProductIds(searchTerm).length) return;

    let applied = false;
    const tryPin = () => {
      if (applied) return;
      if (
        !shouldApplyDelayedExactPin({
          query: searchTerm,
          searchToken,
          currentToken: this.#searchToken,
        })
      ) {
        return;
      }
      const ids = findExactCodeProductIds(searchTerm);
      if (!ids.length) return;
      applied = true;
      this.#pinExactProductCards(ids, abortController);
    };

    ensureSkuIndex({ onPage: tryPin }).then(tryPin, tryPin);
  }

  /**
   * Put exact-code products first. Move an existing card; otherwise fetch via id:0 OR id:X.
   * @param {number[]} ids
   * @param {AbortController} abortController
   */
  async #pinExactProductCards(ids, abortController) {
    if (!ids.length || abortController.signal.aborted) return;

    const root = this.refs.predictiveSearchResults;
    const list = root.querySelector('.predictive-search-results__wrapper-products');
    const missing = ids.filter((id) => !findExactCard(root, id));

    if (!list) {
      if (missing.length) await this.#renderSkuSupplement(ids, abortController);
      return;
    }

    let fetchedRoot = null;
    if (missing.length) {
      const markup = await this.#fetchSkuSupplementMarkup(missing, abortController);
      if (abortController.signal.aborted) return;
      if (markup) fetchedRoot = new DOMParser().parseFromString(markup, 'text/html');
    }

    pinExactCards(list, ids, fetchedRoot);
  }

  /**
   * @param {number[]} ids
   * @param {AbortController} abortController
   * @returns {Promise<string | null>}
   */
  async #fetchSkuSupplementMarkup(ids, abortController) {
    if (!ids.length || abortController.signal.aborted || !this.dataset.sectionId) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', skuSearchIdsQuery(ids.slice(0, 8)));
    url.searchParams.set('type', 'product');
    url.searchParams.set('sku_supplement', '1');

    const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
    if (!markup || abortController.signal.aborted) return null;
    return markup;
  }

  /**
   * @param {number[]} ids
   * @param {AbortController} abortController
   */
  async #renderSkuSupplement(ids, abortController) {
    const markup = await this.#fetchSkuSupplementMarkup(ids, abortController);
    if (!markup) return;

    morph(this.refs.predictiveSearchResults, markup);
    this.#resetScrollPositions();
  }

  /**
   * @param {string} markup
   * @returns {boolean}
   */
  #markupHasProductResults(markup) {
    return /predictive-search-results__card--product/.test(markup);
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
